import React, { useEffect, useMemo, useRef, useState } from "react";

// region Imports - External libraries
import _ from "lodash";
import * as dateFns from "date-fns";
import Loading from "react-fullscreen-loading";
// endregion Imports - External libraries
// region Imports - Shared
import { Vehicle } from "@shared/interfaces/vehicle.interface";
import { IParamQueryValues } from "@shared/interfaces/filter.interface";
import { FilterApplicationTypesID } from "@shared/constants/filter-application-types.enum";
import { InternalCodeErrors } from "@shared/constants/internal-code-errors.enum";
import { ParamNameTypes } from "@shared/constants/param-name-types.enum";
import { Regional } from "@shared/entities/reg_regionals.entity";
import { Group } from "@shared/entities/reg_groups.entity";
import { Driver } from "@shared/interfaces/driver.interface";
import { CanReportData } from "@shared/interfaces/can-report.interface";
import { Plant } from "@shared/entities/reg_plants.entity";
// endregion Imports - Shared
// region Imports - Languages
import useTranslation from "src/translations/useTranslation";
import { FilterMessages, MenuMessages, ToastMessages } from "@shared/languages/interfaces";
// endregion Imports - Languages
// region Imports - Services
import api from "@services/api";
// endregion Imports - Services
// region Imports - Utils
import utils from "@utils/useful-functions";
import { vehicleFilterLabel } from "@utils/vehicle-filter-label";
// endregion Imports - Utils
// region Imports - Hooks
import { useToast } from "@hooks/useToast";
// endregion Imports - Hooks
// region Imports - Components
import ModalFormScheduledReportDescription from "@components/Modal/FormScheduledReportDescription";
// endregion Imports - Components
// region Imports - Organisms
import GenericQueryFilter,
{
  IDateRangeOptions,
  IMultipleSelectionOptions,
  IOnFilterReturn
} from "@organisms/GenericQueryFilter";

// endregion Imports - Organisms

// region Interfaces
interface IProps {
  open: boolean;
  onClose: () => void;
  onFilter:
    (numberOfFilteredOptions: number,
    data: CanReportData[],
    startDate: string,
    finishDate: string) => void;
  generateScheduleReport?: {
    openModal: boolean;
    onCloseModal: () => void;
  };
}
// endregion Interfaces

const QueryFilterCanReport: React.FC<IProps> = ({ open, onClose, onFilter, generateScheduleReport }) => {

  // region Hooks
  const { t } = useTranslation();
  const { addToast } = useToast();
  const paramsFilterRef = useRef<IParamQueryValues[]>([]);
  // endregion Hooks
  // region States
  const [optionsVehicleFilter, setOptionsVehicleFilter] = useState<Vehicle[]>([] as Array<Vehicle>);
  const [optionsDriverFilter, setOptionsDriverFilter] = useState<Driver[]>([] as Array<Driver>);
  const [optionsPlantFilter, setOptionsPlantFilter] = useState<Plant[]>([] as Array<Plant>);
  const [optionsRegionalFilter, setOptionsRegionalFilter] = useState<Regional[]>([] as Array<Regional>);
  const [optionsGroupFilter, setOptionsGroupFilter] = useState<Group[]>([] as Array<Group>);

  const [loadingVehicleFilter, setLoadingVehicleFilter] = useState(true);
  const [loadingDriverFilter, setLoadingDriverFilter] = useState(true);
  const [loadingPlantFilter, setLoadingPlantFilter] = useState(true);
  const [loadingRegionalFilter, setLoadingRegionalFilter] = useState(true);
  const [loadingGroupFilter, setLoadingGroupFilter] = useState(true);

  const [loadingCanReport, setLoadingCanReport] = useState(false);
  // endregion States
  // region Constants
  const paramNameStartDate = "startDate";
  const paramNameEndDate = "endDate";
  const paramNameVehicle = "idVehicles";
  const paramNameDriver = "idDrivers";
  const paramNamePlant = "idPlants";
  const paramNameRegional = "idRegionals";
  const paramNameGroup = "idGroups";

  const vehicleFilterOptions: IMultipleSelectionOptions = useMemo(() => ({
    isLoadingValues: loadingVehicleFilter,
    labelName: t(FilterMessages.optionVehicles),
    paramName: paramNameVehicle,
    values: optionsVehicleFilter.map((vehicle) => ({ value: vehicle.id_vehicle, label: vehicleFilterLabel(vehicle) }))
  }), [loadingVehicleFilter, optionsVehicleFilter, t]);

  const driverFilterOptions: IMultipleSelectionOptions = useMemo(() => ({
    isLoadingValues: loadingDriverFilter,
    labelName: t(MenuMessages.drivers),
    paramName: paramNameDriver,
    values: optionsDriverFilter.map((driver) => ({
      value: driver.id_driver!,
      label: `${driver.name} - ${driver.cpf}`
    }))
  }), [loadingDriverFilter, optionsDriverFilter, t]);

  const plantFilterOptions: IMultipleSelectionOptions = useMemo(() => ({
    isLoadingValues: loadingPlantFilter,
    labelName: t(FilterMessages.optionPlants),
    paramName: paramNamePlant,
    values: optionsPlantFilter.map(
      (plant) => ({
        value: plant!.location!.id_location!,
        label: plant.name!
      })
    )
  }), [loadingPlantFilter, optionsPlantFilter, t]);

  const regionalFilterOptions: IMultipleSelectionOptions = useMemo(() => ({
    isLoadingValues: loadingRegionalFilter,
    labelName: t(MenuMessages.regionals),
    paramName: paramNameRegional,
    values: optionsRegionalFilter.map(
      (regional) => ({
        value: regional.id_regional!,
        label: `${regional.code} - ${regional.name}`
      })
    )
  }), [loadingRegionalFilter, optionsRegionalFilter, t]);

  const groupFilterOptions: IMultipleSelectionOptions = useMemo(() => ({
    isLoadingValues: loadingGroupFilter,
    labelName: t(MenuMessages.groups),
    paramName: paramNameGroup,
    values: optionsGroupFilter.map(
      (group) => ({
        value: group.id_group!,
        label: `${group.code} - ${group.name}`
      })
    )
  }), [loadingGroupFilter, optionsGroupFilter, t]);

  const defaultStartDate = dateFns.subDays(new Date(), 1);

  defaultStartDate.setHours(0, 0, 0, 0);

  const defaultEndDate = new Date();

  defaultEndDate.setHours(23, 59, 59, 999);

  const dateRangeOptions: IDateRangeOptions = {
    labelName: t(FilterMessages.dateRangeLabel),
    paramNameStartDate,
    paramNameEndDate,
    defaultValues: {
      startDate: utils.convertDateToISOWithTimezone(defaultStartDate),
      endDate: utils.convertDateToISOWithTimezone(defaultEndDate)
    }
  };
  // endregion Constants
  // region Functions

  const loadVehicleOptions = async () => {
    setLoadingVehicleFilter(true);
    try {
      const { data } = await api.get("vehicles/read", { params: { linkedHardware: true, onlyLabel: true } });

      if (data.status === "success") setOptionsVehicleFilter(data.result);
      else addToast({ type: "info", title: t(ToastMessages.alert), description: data.message });
    } catch (error) {
      utils.handleStandardError(error, t, addToast);
    } finally {
      setLoadingVehicleFilter(false);
    }
  };

  const loadPlantOptions = async () => {
    setLoadingPlantFilter(true);
    try {
      const { data } = await api.get("plants/read", { params: { linkedLocation: true } });

      if (data.status === "success") setOptionsPlantFilter(data.result);
      else setOptionsPlantFilter([]);
    } catch (error) {
      utils.handleStandardError(error, t, addToast);
    } finally {
      setLoadingPlantFilter(false);
    }
  };

  const loadDriverOptions = async () => {
    setLoadingDriverFilter(true);
    try {
      const { data } = await api.get("drivers/read");

      if (data.status === "success") setOptionsDriverFilter(data.result as Driver[]);
      else setOptionsDriverFilter([]);
    } catch (error) {
      utils.handleStandardError(error, t, addToast);
    } finally {
      setLoadingDriverFilter(false);
    }
  };

  const loadRegionalOptions = async () => {
    setLoadingRegionalFilter(true);
    try {
      const { data } = await api.get("regionals/read");

      if (data.status === "success") setOptionsRegionalFilter(data.result as Regional[]);
      else setOptionsRegionalFilter([]);
    } catch (error) {
      utils.handleStandardError(error, t, addToast);
    } finally {
      setLoadingRegionalFilter(false);
    }
  };

  const loadGroupOptions = async () => {
    setLoadingGroupFilter(true);
    try {
      const { data } = await api.get("groups/read");

      if (data.status === "success") setOptionsGroupFilter(data.result as Group[]);
      else setOptionsGroupFilter([]);
    } catch (error) {
      utils.handleStandardError(error, t, addToast);
    } finally {
      setLoadingGroupFilter(false);
    }
  };

  const handleFilter = async (paramsData: IParamQueryValues[]) => {
    paramsFilterRef.current = paramsData;
    setLoadingCanReport(true);
    try {
      const mappedParams = paramsData.reduce((acc, param) => {
        acc[param.paramName] = param.paramValue;

        return acc;
      }, {} as { [key: string]: string | string[] });

      const { data } = await api.post("travels/get-can-report", { ...mappedParams });

      const validParamsData = paramsData.filter((item) => !_.isEmpty(item.paramValue));
      const validParamsDataWithoutDate = validParamsData.filter(
        (item) => item.paramName !== paramNameStartDate && item.paramName !== paramNameEndDate
      );

      if (data.status === "alert" && data.errorCode === InternalCodeErrors.VERY_LARGE_FILTER) {
        const additionalFilterOptions: IParamQueryValues[] = [
          { paramName: ParamNameTypes.UTC_OFFSET, paramValue: (utils.getUtcOffset()).toString() }
        ];

        return { status: "scheduledReport", additionalFilterOptions } as IOnFilterReturn;
      }

      if (data.status !== "success") {
        addToast({ type: "info", title: t(ToastMessages.alert), description: data.message });
      }

      const startDateValue = validParamsData.find((item) => item.paramName === paramNameStartDate)?.paramValue as string;
      const finishDateValue = validParamsData.find((item) => item.paramName === paramNameEndDate)?.paramValue as string;

      onFilter(
        validParamsDataWithoutDate.length,
        data.result ?? [],
        startDateValue,
        finishDateValue
      );

      return { status: "success" } as IOnFilterReturn;
    } catch (error) {
      utils.handleStandardError(error, t, addToast);

      return { status: "error" } as IOnFilterReturn;
    } finally {
      setLoadingCanReport(false);
    }
  };

  // endregion Functions
  // region Effects

  useEffect(() => {
    Promise.all([
      loadVehicleOptions(),
      loadDriverOptions(),
      loadPlantOptions(),
      loadRegionalOptions(),
      loadGroupOptions()
    ]).then();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  // endregion Effects

  return (
    <>
      <Loading loading={loadingCanReport} />
      <GenericQueryFilter
        open={open}
        onClose={onClose}
        applicationTypeID={FilterApplicationTypesID.CAN_REPORT}
        onFilter={(paramsData) => handleFilter(paramsData)}
        multipleSelectionOptions={[
          vehicleFilterOptions,
          driverFilterOptions,
          plantFilterOptions,
          regionalFilterOptions,
          groupFilterOptions
        ]}
        dateRangeOptions={dateRangeOptions}
      />
      {generateScheduleReport && (
        <ModalFormScheduledReportDescription
          open={generateScheduleReport.openModal}
          onClose={() => generateScheduleReport.onCloseModal()}
          filter={{
            application_type_id: FilterApplicationTypesID.CAN_REPORT,
            filter_options: paramsFilterRef.current
          }}
          additionalFilterOptions={[]}
        />
      )}
    </>
  );
};

export default QueryFilterCanReport;
